import React from "react";
import { CheckCircle2, Target, Users, BookOpen, Layers, Briefcase, ShieldCheck, Award, Code2, LineChart, Megaphone, Building2, User } from "lucide-react";
import { WHAT_YOULL_LEARN, CAREER_OUTCOMES, HIGHLIGHTS, TARGET_AUDIENCE, SKILLS } from "@/data/certificationProgramData";

const HIGHLIGHT_ICONS = [BookOpen, Briefcase, ShieldCheck, Award];
const AUDIENCE_ICONS = [Layers, LineChart, Megaphone, Building2, Code2, User];

export function CourseOverviewSection() {
  return (
    <div className="mb-10" data-testid="certification-overview-section">
      <h2 className="font-heading text-2xl font-semibold text-white mb-1">Everything you need to become Data Cloud job-ready.</h2>
      <p className="text-sm text-slate-400 mb-6 max-w-2xl">
        Designed from the official Salesforce Data Cloud syllabus — not a generic online tutorial. Every module ties directly
        into a project you'll ship.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
        <div className="glass-panel rounded-2xl p-6" data-testid="certification-what-youll-learn">
          <h3 className="font-heading font-semibold text-white text-sm mb-4 flex items-center gap-2"><Target className="w-4 h-4 text-amber-300" /> What You'll Learn</h3>
          <ul className="space-y-2">
            {WHAT_YOULL_LEARN.map((a, i) => (
              <li key={i} className="text-sm text-slate-300 flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> {a}</li>
            ))}
          </ul>
        </div>
        <div className="glass-panel rounded-2xl p-6" data-testid="certification-career-outcomes">
          <h3 className="font-heading font-semibold text-white text-sm mb-4 flex items-center gap-2"><Award className="w-4 h-4 text-amber-300" /> Career Outcomes</h3>
          <ul className="space-y-2">
            {CAREER_OUTCOMES.map((a, i) => (
              <li key={i} className="text-sm text-slate-300 flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> {a}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="certification-highlights-grid">
        {HIGHLIGHTS.map((h, i) => {
          const Icon = HIGHLIGHT_ICONS[i];
          return (
            <div key={i} className="glass-panel rounded-xl p-5" data-testid={`certification-highlight-${i}`}>
              <Icon className="w-5 h-5 text-amber-300 mb-2.5" />
              <p className="text-sm font-semibold text-white mb-1">{h.title}</p>
              <p className="text-xs text-slate-400 leading-relaxed">{h.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AudienceSection() {
  return (
    <div className="mb-10" data-testid="certification-audience-section">
      <h2 className="font-heading text-2xl font-semibold text-white mb-1 flex items-center gap-2"><Users className="w-5 h-5 text-amber-300" /> Built for every stage of your career.</h2>
      <p className="text-sm text-slate-400 mb-5">Who should join this program.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="certification-audience-panel">
        {TARGET_AUDIENCE.map((a, i) => {
          const Icon = AUDIENCE_ICONS[i % AUDIENCE_ICONS.length];
          return (
            <div key={i} className="glass-panel rounded-xl p-5 flex items-start gap-3" data-testid={`certification-audience-${i}`}>
              <Icon className="w-4.5 h-4.5 text-amber-300 mt-0.5 shrink-0" />
              <p className="text-sm text-slate-300 leading-relaxed">{a}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SkillsSection() {
  return (
    <div className="mb-10" data-testid="certification-skills-section">
      <h2 className="font-heading text-2xl font-semibold text-white mb-1">16 hands-on skills, industry-mapped.</h2>
      <p className="text-sm text-slate-400 mb-5">Skills You'll Gain</p>
      <div className="flex flex-wrap gap-2" data-testid="certification-skills-grid">
        {SKILLS.map((s, i) => (
          <span key={i} className="text-xs font-medium text-amber-200 bg-amber-400/10 border border-amber-400/25 rounded-full px-3.5 py-1.5" data-testid={`certification-skill-${i}`}>
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}
